import { errorMessages } from "app";
import { maxValue, minValue, regex, required } from "react-admin";
import { ALLOWED_FILE_EXTENSIONS } from "./constants";

// Extensions are a parameter so a caller can allow a different set; the default covers the common case.
export const validateFileType =
  ({ isRequired = false, extensions = ALLOWED_FILE_EXTENSIONS } = {}) =>
  value => {
    if (!value) return isRequired ? "Файлът е задължителен." : undefined;

    const fileName = value?.rawFile?.name?.toLowerCase() || value?.src?.toLowerCase?.();

    if (!fileName) return "Файлът е невалиден.";

    return extensions.some(extension => fileName.endsWith(extension)) ? undefined : "Невалиден формат!";
  };

export const validateSecondName = value => {
  if (!value) return undefined;
  if (value.length < 2 || value.length > 30) {
    return "Моля, въведете между 2 и 30 символа, или оставете полето празно.";
  }
  return undefined;
};

export const validateExactLength = length => value => {
  if (!value) return "Задължително";
  if (value.length !== length) return `Полето трябва да е точно ${length} символа`;
  return undefined;
};

export const validateComment = value => {
  if (!value) return undefined;
  if (value.length > 100) {
    return "Моля, въведете коментар до 100 символа, или оставете полето празно.";
  }
  return undefined;
};
export const idFileGenerate = () => {
  const now = new Date();

  const year = String(now.getFullYear()).slice(-2);
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  const hour = String(now.getHours()).padStart(2, "0");
  const minute = String(now.getMinutes()).padStart(2, "0");
  const second = String(now.getSeconds()).padStart(8, "0");

  const dateStr = `${year}${month}${day}${hour}${minute}${second}`;

  return `${dateStr}`;
};
export const numberValidator = regex(/^-?\d+(\.\d+)?$/, "Моля, въведете валидно число (цяло или десетично)");
export const integerValidator = value =>
  value == null || Number.isInteger(Number(value)) ? undefined : "Моля, въведете цяло число.";

export const nameCyrillicRegexValidator = regex(/^[а-я- ]*$/i, "Полето трябва да съдържа само букви на кирилица");
export const nameLatinRegexValidator = regex(/^[a-z- ]*$/i, "Полето трябва да съдържа само букви на латиница");

export const validateDateNotInFuture = value => {
  if (!value) return undefined;
  const today = new Date();
  today.setHours(23, 59, 59, 999);
  return new Date(value) > today ? "Датата не може да бъде в бъдещето" : undefined;
};

export const validateStreamGroup =
  (isRequired = false) =>
  value => {
    if (!value) {
      return isRequired ? "Полето е задължително" : undefined;
    }
    const arr = value?.split(",").map(item => item.trim()) || [];

    return arr.some(item => isNaN(parseInt(item)) || (parseInt(item) < 1 && isRequired))
      ? "Невалидни стойности"
      : undefined;
  };
export const documentNumberValidator =
  (message = "Позволени са само големи латински букви, цифри, интервал и тире!") =>
  value => {
    if (!value) return undefined;
    return /^[A-Z0-9 -]+$/.test(value) ? undefined : message;
  };
export const noCyrillic =
  (message = "Въведените данни не трябва да съдържат букви на кирилица!") =>
  value => {
    if (value && /[\u0400-\u04FF\u0500-\u052F\u2DE0-\u2DFF\uA640-\uA69F\u1C80-\u1C88\uFE2E-\uFE2F]/.test(value)) {
      return message;
    }
    return undefined;
  };
export const onlyDigits = value => {
  if (!value) return "Задължително";
  return /^\d+$/.test(String(value)) ? undefined : "Позволени са само цифри";
};

export const pidValidator =
  (message = "Позволени са само големи латински букви, цифри, интервал и тире!") =>
  value => {
    if (!value) return undefined;
    return /^[A-Z0-9 -]+$/.test(value) ? undefined : message;
  };
export const nineDigits = value => {
  if (!value) return "Задължително";
  return String(value).length === 9 ? undefined : "Трябва да бъде точно 9 цифри";
};
export const validateStart = (
  start,
  end,
  required = false,
  message = "Началната дата трябва да е по-малка от крайната",
  campare = null,
  campareMessage = null,
) => {
  if (!start && !required) return undefined;
  if (!start && required) {
    return "Полето е задължително";
  }
  if (start && campare && campare >= start) {
    return campareMessage;
  }
  if (start >= end && required) {
    return message;
  }
};

export const validateEnd = (
  end,
  start,
  required = false,
  message = "Крайната дата трябва да е по-голяма от началната",
) => {
  if (!end && !required) return undefined;
  if (!end && required) {
    return "Полето е задължително";
  }
  if (end <= start && required) {
    return message;
  }
};

export const checkProps = (error, notify) => {
  const groupConflict = error?.properties
    ?.find(item => item.startsWith("StudyGroup:"))
    ?.split(":")[1]
    .trim();
  const baseMessage = errorMessages[error?.errorCode] || errorMessages.default;
  notify(`${baseMessage}${groupConflict !== undefined ? `: ${groupConflict}` : ""}`, {
    type: "error",
  });
};

export const phoneValidator = regex(/^\d+$/, "Полето трябва да съдържа само цифри");
export const urlValidator = regex(
  /^https?:\/\/\S+$/,
  "Моля, въведете валиден линк, който започва с http:// или https://",
);
export const validateDiplomaGrade = [
  required("Полето е задължително"),
  minValue(2, "Оценката не може да е по-малка от 2.00"),
  maxValue(6, "Оценката не може да е по-голяма от 6.00"),
  value => (/^\d+(\.\d{1,2})?$/.test(value) ? undefined : "Позволени са до 2 цифри след десетичната точка"),
];

// Same scale as validateDiplomaGrade, for grades the backend accepts as null.
export const validateOptionalGrade = [
  minValue(2, "Оценката не може да е по-малка от 2.00"),
  maxValue(6, "Оценката не може да е по-голяма от 6.00"),
  value =>
    value == null || value === "" || /^\d+(\.\d{1,2})?$/.test(value)
      ? undefined
      : "Позволени са до 2 цифри след десетичната точка",
];

// ORCID iD: 0000-0000-0000-000X, last char is an ISO 7064 MOD 11-2 checksum (0-9 or X).
export const orcidValidator = value => {
  if (!value) return undefined;
  if (!/^\d{4}-\d{4}-\d{4}-\d{3}[\dX]$/.test(value)) {
    return "Въведете ORCID във формат 0000-0000-0000-000X";
  }
  const digits = value.replace(/-/g, "");
  const total = [...digits.slice(0, 15)].reduce((sum, digit) => (sum + Number(digit)) * 2, 0);
  const result = (12 - (total % 11)) % 11;
  const checkChar = result === 10 ? "X" : String(result);
  return digits[15] === checkChar ? undefined : "Невалиден ORCID номер";
};
